import React, { useState, useEffect } from 'react'
import {
  AlertTriangle,
  Target,
  TrendingUp,
  Settings,
  Info,
  Download,
  RefreshCw
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useToastHelpers } from '@/contexts/ToastContext'
import { apiClient, PropertyResponse } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { NavigationHeader } from '@/components/ui/NavigationHeader'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { RiskScoreOverview } from '@/components/risk/RiskScoreOverview'
import { PortfolioRiskHeatMap } from '@/components/risk/PortfolioRiskHeatMap'
import { ConcentrationRiskVisualizations } from '@/components/risk/ConcentrationRiskVisualizations'
import { MonteCarloSimulation } from '@/components/risk/MonteCarloSimulation'
import { StressTestScenarioBuilder } from '@/components/risk/StressTestScenarioBuilder'
import { RiskAdjustedReturnsMetrics } from '@/components/risk/RiskAdjustedReturnsMetrics'
import { RiskAlertsPanel } from '@/components/risk/RiskAlertsPanel'
import { AlertsSummary } from '@/components/risk/AlertsSummary'
import { EducationalRiskContent } from '@/components/risk/EducationalRiskContent'
import { generateMockRiskData } from '@/utils/riskCalculations'
import { generateMockRiskAlerts, shouldUseMockAlerts } from '@/utils/mockRiskAlerts'
import productionDebugger from '@/utils/productionDebug'
import type {
  RiskAssessmentResponse,
  RiskMetrics,
  MonteCarloResults,
  StressTestScenario,
  ScenarioImpact,
  RiskAlert
} from '@/types/risk-assessment'

type ActiveTab = 'overview' | 'concentration' | 'monte-carlo' | 'stress-test' | 'returns' | 'education'

export function RiskAssessmentPage() {
  const { user } = useAuth()
  const { showSuccess, showError } = useToastHelpers()

  // State management
  const [properties, setProperties] = useState<PropertyResponse[]>([])
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessmentResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview')
  const [riskAlerts, setRiskAlerts] = useState<RiskAlert[]>([])
  const [monteCarloResults, setMonteCarloResults] = useState<MonteCarloResults | null>(null)
  const [stressTestResults, setStressTestResults] = useState<Record<string, ScenarioImpact>>({})

  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      setLoading(true)

      // Load portfolio properties
      const propertiesResponse = await apiClient.getUserProperties({ limit: 100 })
      setProperties(propertiesResponse.properties)

      // If we have properties, load risk assessment
      if (propertiesResponse.properties.length > 0) {
        await loadRiskAssessment(propertiesResponse.properties)
        await loadRiskAlerts()
      } else {
        // Set empty assessment data for no properties case
        setRiskAssessment(null)
      }
    } catch (error) {
      console.error('Failed to load risk assessment data:', error)
      showError('Loading Failed', 'Failed to load risk assessment data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadRiskAssessment = async (portfolioProperties: PropertyResponse[]) => {
    try {
      const propertyIds = portfolioProperties.map(p => p.property_id)
      const request = {
        properties: propertyIds
      }

      try {
        // Try to load from API first
        const assessment = await apiClient.getRiskAssessment(request)
        console.log('🔍 Raw assessment from API:', JSON.stringify(assessment, null, 2))
        console.log('✅ Raw API Response received:', assessment)
        console.log('📊 Overall risk score from backend:', assessment?.risk_metrics?.overall_risk_score || (assessment as any)?.overall_risk_score || (assessment as any)?.data?.overall_risk_score)

        // Validate and normalize the response structure
        const validatedAssessment = validateAndNormalizeAssessment(assessment)
        console.log('✅ Validated assessment:', JSON.stringify(validatedAssessment, null, 2))
        console.log('📊 Geographic locations count:', validatedAssessment?.risk_metrics?.concentration_risk?.geographic?.locations?.length || 0)
        console.log('🏘️ Property types count:', validatedAssessment?.risk_metrics?.concentration_risk?.property_type?.types?.length || 0)
        console.log('✅ Validated Assessment final result:', validatedAssessment)
        console.log('📊 Final overall risk score:', validatedAssessment?.risk_metrics?.overall_risk_score)

        // Store detailed debug info for production troubleshooting
        try {
          const debugSummary = {
            timestamp: new Date().toISOString(),
            dataSource: 'api',
            overallRiskScore: validatedAssessment?.risk_metrics?.overall_risk_score,
            geoLocationsCount: validatedAssessment?.risk_metrics?.concentration_risk?.geographic?.locations?.length || 0,
            propertyTypesCount: validatedAssessment?.risk_metrics?.concentration_risk?.property_type?.types?.length || 0,
            marketsCount: validatedAssessment?.risk_metrics?.concentration_risk?.market_exposure?.markets?.length || 0,
            validationSuccess: true
          }
          sessionStorage.setItem('riskAssessment_validationResult', JSON.stringify(debugSummary))
        } catch (e) {
          console.warn('Failed to store validation debug data:', e)
        }

        // Verify we got valid data before setting it
        if (validatedAssessment?.risk_metrics?.overall_risk_score) {
          console.log('✅ Successfully processed backend risk assessment data')
          setRiskAssessment(validatedAssessment)
        } else {
          throw new Error('Validation failed - no valid risk score received')
        }
      } catch (apiError) {
        // Fall back to mock data for development/demo
        console.warn('❌ Risk assessment API error or validation failed, using mock data:', apiError)

        const mockRiskMetrics = generateMockRiskData(portfolioProperties)
        console.log('Generated mock risk metrics:', mockRiskMetrics)

        // Enhanced mock data structure that matches test data patterns
        const enhancedMockData = {
          concentration_risk: {
            geographic: {
              locations: [
                {
                  city: "Austin",
                  state: "TX",
                  zip_code: "78701",
                  percentage: 50.0,
                  property_count: 2,
                  total_value: 850000,
                  risk_level: "medium" as const
                },
                {
                  city: "Phoenix",
                  state: "AZ",
                  zip_code: "85001",
                  percentage: 50.0,
                  property_count: 2,
                  total_value: 750000,
                  risk_level: "medium" as const
                }
              ],
              concentration_score: 68.0,
              diversification_recommendation: "Mock data - Consider geographic diversification"
            },
            property_type: {
              types: [
                {
                  type: "single-family" as const,
                  percentage: 75.0,
                  property_count: 3,
                  total_value: 1200000,
                  risk_level: "medium" as const
                },
                {
                  type: "multi-family" as const,
                  percentage: 25.0,
                  property_count: 1,
                  total_value: 400000,
                  risk_level: "low" as const
                }
              ],
              concentration_score: 65.0,
              recommendation: "Mock data - Consider property type diversification"
            },
            tenant_type: {
              tenant_quality_score: 75,
              high_risk_tenants: 0,
              average_lease_length: 12,
              tenant_turnover_rate: 15
            },
            market_exposure: {
              markets: [
                {
                  market_name: "Austin Metro",
                  exposure_percentage: 50.0,
                  market_health_score: 85,
                  growth_trend: "growing" as const,
                  employment_rate: 95.2,
                  population_growth: 2.8
                },
                {
                  market_name: "Phoenix Metro",
                  exposure_percentage: 50.0,
                  market_health_score: 78,
                  growth_trend: "stable" as const,
                  employment_rate: 94.1,
                  population_growth: 1.9
                }
              ],
              correlation_score: 0.3
            }
          }
        }

        const mockAssessment: RiskAssessmentResponse = {
          portfolio_id: 'mock-portfolio',
          analysis_date: new Date().toISOString(),
          risk_metrics: {
            ...mockRiskMetrics,
            concentration_risk: enhancedMockData.concentration_risk
          },
          risk_adjusted_returns: {
            sharpe_ratio: 1.2,
            sortino_ratio: 1.8,
            calmar_ratio: 0.9,
            information_ratio: 0.4,
            risk_adjusted_cap_rate: 7.8,
            risk_adjusted_irr: 12.5,
            volatility_metrics: {
              annual_volatility: 14.2,
              downside_deviation: 8.7,
              maximum_drawdown: 15.3,
              beta_to_market: 0.85
            },
            benchmark_comparison: {
              market_index: 'REIT Index',
              excess_return: 3.2,
              tracking_error: 4.1,
              alpha: 2.8
            }
          },
          portfolio_optimization: {
            current_allocation: {
              by_geography: [],
              by_property_type: [],
              by_risk_level: [],
              cash_position: 15000,
              leverage_ratio: mockRiskMetrics.leverage_ratio
            },
            efficient_frontier: [],
            optimization_suggestions: [],
            target_allocation: {
              by_geography: [],
              by_property_type: [],
              by_risk_level: [],
              cash_position: 25000,
              leverage_ratio: 65
            }
          },
          risk_alerts: [],
          educational_content: {
            risk_glossary: [],
            best_practices: [],
            industry_benchmarks: []
          }
        }

        console.log('Setting mock assessment:', mockAssessment)

        // Store mock data debug info
        try {
          const mockDebugSummary = {
            timestamp: new Date().toISOString(),
            dataSource: 'mock',
            overallRiskScore: mockAssessment.risk_metrics.overall_risk_score,
            geoLocationsCount: mockAssessment.risk_metrics.concentration_risk.geographic.locations.length,
            propertyTypesCount: mockAssessment.risk_metrics.concentration_risk.property_type.types.length,
            marketsCount: mockAssessment.risk_metrics.concentration_risk.market_exposure.markets.length,
            mockDataUsed: true,
            apiError: (apiError instanceof Error ? apiError.message : String(apiError)) || 'Unknown API error'
          }
          sessionStorage.setItem('riskAssessment_validationResult', JSON.stringify(mockDebugSummary))
        } catch (e) {
          console.warn('Failed to store mock debug data:', e)
        }
        setRiskAssessment(mockAssessment)
      }
    } catch (error) {
      console.error('Failed to load risk assessment:', error)
      throw error
    }
  }

  const loadRiskAlerts = async (portfolioId?: string) => {
    try {
      // Get only unacknowledged alerts with high and critical severity
      const response = await apiClient.getRiskAlerts(portfolioId, {
        acknowledged: false,
        limit: 50 // Reasonable limit for UI display
      })
      setRiskAlerts(response.alerts || [])

      // Log alert summary for debugging
      if (response.alerts && response.alerts.length > 0) {
        const criticalCount = response.alerts.filter(a => a.severity === 'critical').length
        const highCount = response.alerts.filter(a => a.severity === 'high').length
        console.log(`Loaded ${response.alerts.length} risk alerts (${criticalCount} critical, ${highCount} high)`)
      }
    } catch (error) {
      // Enhanced error handling for different scenarios
      if (error instanceof Error) {
        if (error.message.includes('Authentication required')) {
          console.warn('Authentication issue when loading alerts:', error.message)
          showError('Authentication Required', 'Please log in again to view risk alerts.')
          return
        }

        if (error.message.includes('Network error')) {
          console.warn('Network issue when loading alerts:', error.message)
          showError('Network Error', 'Unable to load risk alerts. Please check your connection.')
          return
        }

        if (error.message.includes('Server error')) {
          console.warn('Server issue when loading alerts:', error.message)
          showError('Server Error', 'Risk alerts service is temporarily unavailable.')
          return
        }
      }

      // For 404 or other expected errors (endpoint not implemented), use mock data in development
      console.info('Risk alerts not available:', error)

      if (shouldUseMockAlerts() && properties.length > 0) {
        console.log('Using mock risk alerts for development/testing')
        const mockAlerts = generateMockRiskAlerts(properties)
        setRiskAlerts(mockAlerts)
      } else {
        setRiskAlerts([])
      }
    }
  }

  const handleRefreshAssessment = async () => {
    if (properties.length === 0) return

    try {
      setRefreshing(true)
      await loadRiskAssessment(properties)
      await loadRiskAlerts()
      showSuccess('Assessment Updated', 'Risk assessment has been refreshed with latest data.')
    } catch (error) {
      showError('Refresh Failed', 'Failed to refresh risk assessment. Please try again.')
    } finally {
      setRefreshing(false)
    }
  }

  const handleRunMonteCarloSimulation = async (params: any) => {
    try {
      const results = await apiClient.runMonteCarloSimulation(params)
      setMonteCarloResults(results)
      showSuccess('Simulation Complete', 'Monte Carlo simulation has completed successfully.')

      // Refresh alerts after simulation as new risks may have been identified
      setTimeout(() => loadRiskAlerts(), 500)

      return results
    } catch (error) {
      console.error('Monte Carlo simulation failed:', error)
      showError('Simulation Failed', 'Failed to run Monte Carlo simulation. Please check your parameters and try again.')
      throw error
    }
  }

  const handleRunStressTest = async (scenario: StressTestScenario) => {
    try {
      const impact = await apiClient.runStressTest(scenario)
      setStressTestResults(prev => ({
        ...prev,
        [scenario.scenario_id]: impact
      }))

      // Refresh alerts after stress test as new risks may have been identified
      setTimeout(() => loadRiskAlerts(), 500)

      return impact
    } catch (error) {
      showError('Stress Test Failed', 'Failed to run stress test scenario. Please try again.')
      throw error
    }
  }

  const handleExportRiskReport = () => {
    // TODO: Implement PDF export functionality
    showSuccess('Export Started', 'Risk assessment report will be generated and downloaded.')
  }

  // Helper function to validate and normalize the API response
  const validateAndNormalizeAssessment = (assessment: any): RiskAssessmentResponse => {
    // Log the raw assessment for debugging
    console.log('validateAndNormalizeAssessment - Raw backend response:', JSON.stringify(assessment, null, 2))

    // Handle if assessment is wrapped in a response object
    const data = (assessment as any)?.data || assessment

    // Ensure we have a valid object
    if (!data || typeof data !== 'object') {
      console.error('Invalid assessment data:', data)
      console.error('Full assessment object:', assessment)
      throw new Error('Invalid API response: expected object')
    }

    console.log('validateAndNormalizeAssessment - Extracted data:', JSON.stringify(data, null, 2))

    // Build a validated response PRESERVING backend values
    // Backend might send data at different levels - detect the structure
    const riskMetrics = data.risk_metrics || data // Risk metrics might be at root level
    const riskAdjustedReturns = data.risk_adjusted_returns || data.return_analysis // Different field names
    const monteCarloResults = data.monte_carlo_simulation?.results || data.monte_carlo_results

    console.log('🔍 Detected risk metrics source:', riskMetrics === data ? 'root level' : 'nested')
    console.log('🔍 Overall risk score detected:', riskMetrics?.overall_risk_score)
    console.log('🔍 Backend raw structure check:')
    console.log('  - concentration_risk exists:', !!data.concentration_risk)
    console.log('  - liquidity_assessment exists:', !!data.liquidity_assessment)
    console.log('  - leverage_analysis exists:', !!data.leverage_analysis)
    console.log('  - stress_test_results exists:', !!data.stress_test_results)

    const normalized = {
      portfolio_id: data.portfolio_id || 'default-portfolio',
      analysis_date: data.analysis_date || new Date().toISOString(),
      risk_metrics: normalizeRiskMetrics(riskMetrics),
      monte_carlo_simulation: monteCarloResults ? {
        parameters: data.monte_carlo_simulation?.parameters || {
          simulation_runs: 1000,
          time_horizon_years: 5,
          variables: []
        },
        results: monteCarloResults
      } : undefined,
      risk_adjusted_returns: normalizeRiskAdjustedReturns(riskAdjustedReturns),
      portfolio_optimization: normalizePortfolioOptimization(data.portfolio_optimization),
      risk_alerts: Array.isArray(data.risk_alerts) ? data.risk_alerts : [],
      educational_content: normalizeEducationalContent(data.educational_content)
    }

    console.log('validateAndNormalizeAssessment - Normalized result:', JSON.stringify(normalized, null, 2))
    return normalized
  }

  // Normalize risk metrics with proper defaults
  const normalizeRiskMetrics = (metrics: any): RiskMetrics => {
    console.log('normalizeRiskMetrics - Input metrics:', JSON.stringify(metrics, null, 2))

    if (!metrics || typeof metrics !== 'object') {
      console.warn('normalizeRiskMetrics - No valid metrics found, using defaults')
      return generateDefaultRiskMetrics()
    }

    // PRESERVE backend data - properly extract from nested structures
    const normalized = {
      // Extract overall_risk_score from backend (43.5) - preserve exact value
      overall_risk_score: typeof metrics.overall_risk_score === 'number' ?
        parseFloat(metrics.overall_risk_score.toFixed(2)) : 50,

      concentration_risk: {
        geographic: normalizeGeographicConcentration(metrics.concentration_risk?.geographic),
        property_type: normalizePropertyTypeConcentration(metrics.concentration_risk?.property_type),
        tenant_type: normalizeTenantConcentration(metrics.concentration_risk?.tenant_type),
        market_exposure: normalizeMarketExposure(metrics.concentration_risk?.market_exposure)
      },

      // Extract liquidity score from backend nested structure
      liquidity_score: typeof metrics.liquidity_score === 'number' ?
        parseFloat(metrics.liquidity_score.toFixed(2)) :
        (typeof metrics.liquidity_assessment?.portfolio_score === 'number' ?
          parseFloat(metrics.liquidity_assessment.portfolio_score.toFixed(2)) : 50),

      // Extract leverage ratio from backend nested structure
      leverage_ratio: typeof metrics.leverage_ratio === 'number' ?
        parseFloat(metrics.leverage_ratio.toFixed(2)) :
        (typeof metrics.leverage_analysis?.portfolio_ltv === 'number' ?
          parseFloat(metrics.leverage_analysis.portfolio_ltv.toFixed(2)) : 65),

      vacancy_risk_score: typeof metrics.vacancy_risk_score === 'number' ?
        parseFloat(metrics.vacancy_risk_score.toFixed(2)) : 30,
      market_correlation: typeof metrics.market_correlation === 'number' ?
        parseFloat(metrics.market_correlation.toFixed(2)) : 0.5,
      stress_test_results: normalizeStressTestResults(metrics.stress_test_results)
    }

    console.log('✅ normalizeRiskMetrics - Final normalized output:')
    console.log('📊 Overall Risk Score:', normalized.overall_risk_score)
    console.log('💧 Liquidity Score:', normalized.liquidity_score)
    console.log('⚖️ Leverage Ratio:', normalized.leverage_ratio)
    console.log('🌍 Geographic Locations:', normalized.concentration_risk.geographic.locations.length)
    return normalized
  }

  const normalizeGeographicConcentration = (geo: any) => {
    // Production-safe debugging that works in both dev and prod
    const debugInfo = {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'unknown',
      inputType: typeof geo,
      inputKeys: geo ? Object.keys(geo) : [],
      hasLocations: !!geo?.locations,
      hasDistribution: !!geo?.distribution,
      locationsIsArray: Array.isArray(geo?.locations),
      distributionIsArray: Array.isArray(geo?.distribution),
      locationsLength: Array.isArray(geo?.locations) ? geo.locations.length : 0,
      distributionLength: Array.isArray(geo?.distribution) ? geo.distribution.length : 0
    }

    // Store debug info in sessionStorage for production debugging
    try {
      const existingDebug = JSON.parse(sessionStorage.getItem('riskAssessment_geoDebug') || '[]')
      existingDebug.push(debugInfo)
      sessionStorage.setItem('riskAssessment_geoDebug', JSON.stringify(existingDebug.slice(-10))) // Keep last 10 entries
    } catch (e) {
      // Ignore storage errors
    }

    console.log('🌍 normalizeGeographicConcentration - Input:', JSON.stringify(geo, null, 2))
    console.log('🔍 Production Debug Info:', debugInfo)

    // Enhanced data extraction - handle multiple possible API response formats
    let locations: any[] = []

    // Try multiple possible locations for the data
    if (Array.isArray(geo?.locations)) {
      locations = geo.locations
      console.log('✅ Found locations array directly')
    } else if (Array.isArray(geo?.distribution)) {
      locations = geo.distribution
      console.log('✅ Found distribution array, using as locations')
    } else if (Array.isArray(geo?.geographic_distribution)) {
      locations = geo.geographic_distribution
      console.log('✅ Found geographic_distribution array, using as locations')
    } else if (geo?.geographic && Array.isArray(geo.geographic.locations)) {
      locations = geo.geographic.locations
      console.log('✅ Found nested geographic.locations array')
    } else {
      console.warn('⚠️ No valid geographic data arrays found in:', geo)
    }

    // Normalize location objects to match expected structure
    const normalizedLocations = locations.map((location: any, index: number) => {
      // Handle different possible field names from API
      const normalizedLocation = {
        city: location.city || location.location_name || location.name || `Location ${index + 1}`,
        state: location.state || location.state_code || location.region || 'Unknown',
        zip_code: location.zip_code || location.zipcode || location.postal_code || '00000',
        percentage: typeof location.percentage === 'number' ? location.percentage :
                   typeof location.allocation === 'number' ? location.allocation :
                   typeof location.weight === 'number' ? location.weight * 100 : 0,
        property_count: typeof location.property_count === 'number' ? location.property_count :
                       typeof location.properties === 'number' ? location.properties :
                       typeof location.count === 'number' ? location.count : 1,
        total_value: typeof location.total_value === 'number' ? location.total_value :
                    typeof location.value === 'number' ? location.value :
                    typeof location.market_value === 'number' ? location.market_value : 0,
        risk_level: location.risk_level || location.risk ||
                   (typeof location.percentage === 'number' && location.percentage > 60 ? 'high' :
                    typeof location.percentage === 'number' && location.percentage > 30 ? 'medium' : 'low') as 'low' | 'medium' | 'high'
      }
      return normalizedLocation
    })

    // Additional debugging for locations data
    if (normalizedLocations.length > 0) {
      const locationSample = normalizedLocations[0]
      const locationDebug = {
        sampleLocation: locationSample,
        hasRequiredFields: !!(locationSample?.city && locationSample?.state &&
                            typeof locationSample?.percentage === 'number' &&
                            typeof locationSample?.property_count === 'number' &&
                            typeof locationSample?.total_value === 'number'),
        totalLocations: normalizedLocations.length
      }

      console.log('📍 Location validation:', locationDebug)

      try {
        sessionStorage.setItem('riskAssessment_locationDebug', JSON.stringify(locationDebug))
      } catch (e) {
        // Ignore storage errors
      }
    }

    // Enhanced concentration score extraction
    const concentration_score = typeof geo?.concentration_score === 'number' ?
      parseFloat(geo.concentration_score.toFixed(2)) :
      (typeof geo?.city_concentration_hhi === 'number' ?
        parseFloat((geo.city_concentration_hhi * 100).toFixed(2)) :
        (typeof geo?.hhi === 'number' ?
          parseFloat((geo.hhi * 100).toFixed(2)) :
          (typeof geo?.concentration === 'number' ? geo.concentration : 50)))

    const normalized = {
      locations: normalizedLocations,
      concentration_score: concentration_score,
      diversification_recommendation: geo?.diversification_recommendation ||
                                     geo?.recommendation ||
                                     geo?.advice ||
                                     'Unable to analyze - no data available'
    }

    // Final validation - ensure all location objects have required structure
    const validatedLocations = normalized.locations.filter((location: any) => {
      const isValid = location &&
                     typeof location.city === 'string' &&
                     typeof location.state === 'string' &&
                     typeof location.percentage === 'number' &&
                     typeof location.property_count === 'number' &&
                     typeof location.total_value === 'number'

      if (!isValid) {
        console.warn('⚠️ Invalid location filtered out:', location)
      }
      return isValid
    })

    if (validatedLocations.length !== normalized.locations.length) {
      console.warn(`⚠️ Geographic data validation: ${normalized.locations.length - validatedLocations.length} invalid locations filtered out`)
    }

    const finalResult = {
      ...normalized,
      locations: validatedLocations
    }

    console.log('✅ normalizeGeographicConcentration - Final Result:')
    console.log('📍 Locations count:', finalResult.locations.length)
    console.log('📊 Concentration Score:', finalResult.concentration_score)
    if (finalResult.locations.length > 0) {
      console.log('🏠 Sample location:', finalResult.locations[0])
      console.log('🗺 All locations:', finalResult.locations.map(l => `${l.city}, ${l.state} (${l.percentage}%)`).join('; '))
    }

    // Store final result for production debugging
    try {
      sessionStorage.setItem('riskAssessment_finalGeoData', JSON.stringify({
        timestamp: new Date().toISOString(),
        locationsCount: finalResult.locations.length,
        concentrationScore: finalResult.concentration_score,
        sampleLocation: finalResult.locations[0] || null,
        allLocations: finalResult.locations
      }))
    } catch (e) {
      // Ignore storage errors
    }

    return finalResult
  }

  const normalizePropertyTypeConcentration = (prop: any) => {
    console.log('🏘️ normalizePropertyTypeConcentration - Input:', JSON.stringify(prop, null, 2))

    // Enhanced data extraction - handle multiple possible API response formats
    let types: any[] = []

    // Try multiple possible locations for the data
    if (Array.isArray(prop?.types)) {
      types = prop.types
      console.log('✅ Found types array directly')
    } else if (Array.isArray(prop?.distribution)) {
      types = prop.distribution
      console.log('✅ Found distribution array, using as types')
    } else if (Array.isArray(prop?.property_type_distribution)) {
      types = prop.property_type_distribution
      console.log('✅ Found property_type_distribution array, using as types')
    } else if (prop?.property_type && Array.isArray(prop.property_type.types)) {
      types = prop.property_type.types
      console.log('✅ Found nested property_type.types array')
    } else {
      console.warn('⚠️ No valid property type data arrays found in:', prop)
    }

    // Normalize type objects to match expected structure
    const normalizedTypes = types.map((type: any, index: number) => {
      const normalizedType = {
        type: type.type || type.property_type || type.category || `Type ${index + 1}`,
        percentage: typeof type.percentage === 'number' ? type.percentage :
                   typeof type.allocation === 'number' ? type.allocation :
                   typeof type.weight === 'number' ? type.weight * 100 : 0,
        property_count: typeof type.property_count === 'number' ? type.property_count :
                       typeof type.properties === 'number' ? type.properties :
                       typeof type.count === 'number' ? type.count : 1,
        total_value: typeof type.total_value === 'number' ? type.total_value :
                    typeof type.value === 'number' ? type.value :
                    typeof type.market_value === 'number' ? type.market_value : 0,
        risk_level: type.risk_level || type.risk ||
                   (typeof type.percentage === 'number' && type.percentage > 60 ? 'high' :
                    typeof type.percentage === 'number' && type.percentage > 30 ? 'medium' : 'low') as 'low' | 'medium' | 'high'
      }
      return normalizedType
    })

    // Enhanced concentration score extraction
    const concentration_score = typeof prop?.concentration_score === 'number' ?
      parseFloat(prop.concentration_score.toFixed(2)) :
      (typeof prop?.concentration_hhi === 'number' ?
        parseFloat((prop.concentration_hhi * 100).toFixed(2)) :
        (typeof prop?.hhi === 'number' ?
          parseFloat((prop.hhi * 100).toFixed(2)) : 50))

    const normalized = {
      types: normalizedTypes,
      concentration_score: concentration_score,
      recommendation: prop?.recommendation || prop?.advice || 'Unable to analyze - no data available'
    }

    console.log('✅ normalizePropertyTypeConcentration - Final Result:')
    console.log('🏠 Property types count:', normalized.types.length)
    console.log('📊 Concentration Score:', normalized.concentration_score)
    if (normalized.types.length > 0) {
      console.log('🏘️ Sample type:', normalized.types[0])
      console.log('🏠 All types:', normalized.types.map(t => `${t.type} (${t.percentage}%)`).join('; '))
    }
    return normalized
  }

  const normalizeTenantConcentration = (tenant: any) => ({
    tenant_quality_score: tenant?.tenant_quality_score ?? 50,
    high_risk_tenants: tenant?.high_risk_tenants ?? 0,
    average_lease_length: tenant?.average_lease_length ?? 12,
    tenant_turnover_rate: tenant?.tenant_turnover_rate ?? 0
  })

  const normalizeMarketExposure = (market: any) => ({
    markets: Array.isArray(market?.markets) ? market.markets : [],
    correlation_score: market?.correlation_score ?? 0.5
  })

  const normalizeStressTestResults = (stress: any) => {
    console.log('⚡ normalizeStressTestResults - Input:', JSON.stringify(stress, null, 2))

    // Extract stress test scenarios - backend sends as 'scenarios' array
    const scenarios = Array.isArray(stress?.scenarios) ? stress.scenarios : []

    const normalized = {
      scenarios: scenarios,
      worst_case_loss: typeof stress?.worst_case_loss === 'number' ?
        parseFloat(stress.worst_case_loss.toFixed(2)) : 0,
      survival_probability: typeof stress?.survival_probability === 'number' ?
        parseFloat(stress.survival_probability.toFixed(2)) : 100,
      recommended_actions: Array.isArray(stress?.recommended_actions) ? stress.recommended_actions : []
    }

    console.log('✅ normalizeStressTestResults - Extracted:')
    console.log('📋 Scenarios count:', normalized.scenarios.length)
    console.log('💸 Worst case loss:', normalized.worst_case_loss)
    console.log('🎯 Survival probability:', normalized.survival_probability)
    if (normalized.scenarios.length > 0) {
      console.log('⚡ Sample scenario:', normalized.scenarios[0]?.name || normalized.scenarios[0]?.scenario_id)
    }
    return normalized
  }

  const normalizeRiskAdjustedReturns = (returns: any) => {
    console.log('normalizeRiskAdjustedReturns - Input:', JSON.stringify(returns, null, 2))

    if (!returns || typeof returns !== 'object') {
      console.warn('normalizeRiskAdjustedReturns - No valid returns data, using defaults')
      return generateDefaultRiskAdjustedReturns()
    }

    const normalized = {
      sharpe_ratio: typeof returns.sharpe_ratio === 'number' ? returns.sharpe_ratio : 0,
      sortino_ratio: typeof returns.sortino_ratio === 'number' ? returns.sortino_ratio : 0,
      calmar_ratio: typeof returns.calmar_ratio === 'number' ? returns.calmar_ratio : 0,
      information_ratio: typeof returns.information_ratio === 'number' ? returns.information_ratio : 0,
      risk_adjusted_cap_rate: typeof returns.risk_adjusted_cap_rate === 'number' ? returns.risk_adjusted_cap_rate : 0,
      risk_adjusted_irr: typeof returns.risk_adjusted_irr === 'number' ? returns.risk_adjusted_irr : 0,
      volatility_metrics: {
        annual_volatility: typeof returns.volatility_metrics?.annual_volatility === 'number' ? returns.volatility_metrics.annual_volatility : 0,
        downside_deviation: typeof returns.volatility_metrics?.downside_deviation === 'number' ? returns.volatility_metrics.downside_deviation : 0,
        maximum_drawdown: typeof returns.volatility_metrics?.maximum_drawdown === 'number' ? returns.volatility_metrics.maximum_drawdown : 0,
        beta_to_market: typeof returns.volatility_metrics?.beta_to_market === 'number' ? returns.volatility_metrics.beta_to_market : 0
      },
      benchmark_comparison: {
        market_index: returns.benchmark_comparison?.market_index || 'N/A',
        excess_return: typeof returns.benchmark_comparison?.excess_return === 'number' ? returns.benchmark_comparison.excess_return : 0,
        tracking_error: typeof returns.benchmark_comparison?.tracking_error === 'number' ? returns.benchmark_comparison.tracking_error : 0,
        alpha: typeof returns.benchmark_comparison?.alpha === 'number' ? returns.benchmark_comparison.alpha : 0
      }
    }

    console.log('normalizeRiskAdjustedReturns - Output:', JSON.stringify(normalized, null, 2))
    return normalized
  }

  const normalizePortfolioOptimization = (opt: any) => {
    if (!opt || typeof opt !== 'object') {
      return generateDefaultPortfolioOptimization()
    }

    const normalizeAllocation = (alloc: any) => ({
      by_geography: Array.isArray(alloc?.by_geography) ? alloc.by_geography : [],
      by_property_type: Array.isArray(alloc?.by_property_type) ? alloc.by_property_type : [],
      by_risk_level: Array.isArray(alloc?.by_risk_level) ? alloc.by_risk_level : [],
      cash_position: alloc?.cash_position ?? 0,
      leverage_ratio: alloc?.leverage_ratio ?? 65
    })

    return {
      current_allocation: normalizeAllocation(opt.current_allocation),
      efficient_frontier: Array.isArray(opt.efficient_frontier) ? opt.efficient_frontier : [],
      optimization_suggestions: Array.isArray(opt.optimization_suggestions) ? opt.optimization_suggestions : [],
      target_allocation: normalizeAllocation(opt.target_allocation)
    }
  }

  const normalizeEducationalContent = (edu: any) => ({
    risk_glossary: Array.isArray(edu?.risk_glossary) ? edu.risk_glossary : [],
    best_practices: Array.isArray(edu?.best_practices) ? edu.best_practices : [],
    industry_benchmarks: Array.isArray(edu?.industry_benchmarks) ? edu.industry_benchmarks : []
  })

  // Helper functions to generate default values
  const generateDefaultRiskMetrics = (): RiskMetrics => ({
    overall_risk_score: 50,
    concentration_risk: {
      geographic: {
        locations: [],
        concentration_score: 50,
        diversification_recommendation: 'Unable to analyze - no data available'
      },
      property_type: {
        types: [],
        concentration_score: 50,
        recommendation: 'Unable to analyze - no data available'
      },
      tenant_type: {
        tenant_quality_score: 50,
        high_risk_tenants: 0,
        average_lease_length: 12,
        tenant_turnover_rate: 0
      },
      market_exposure: {
        markets: [],
        correlation_score: 0.5
      }
    },
    liquidity_score: 50,
    leverage_ratio: 65,
    vacancy_risk_score: 30,
    market_correlation: 0.5,
    stress_test_results: {
      scenarios: [],
      worst_case_loss: 0,
      survival_probability: 100,
      recommended_actions: []
    }
  })

  const generateDefaultRiskAdjustedReturns = () => ({
    sharpe_ratio: 0,
    sortino_ratio: 0,
    calmar_ratio: 0,
    information_ratio: 0,
    risk_adjusted_cap_rate: 0,
    risk_adjusted_irr: 0,
    volatility_metrics: {
      annual_volatility: 0,
      downside_deviation: 0,
      maximum_drawdown: 0,
      beta_to_market: 0
    },
    benchmark_comparison: {
      market_index: 'N/A',
      excess_return: 0,
      tracking_error: 0,
      alpha: 0
    }
  })

  const generateDefaultPortfolioOptimization = () => ({
    current_allocation: {
      by_geography: [],
      by_property_type: [],
      by_risk_level: [],
      cash_position: 0,
      leverage_ratio: 65
    },
    efficient_frontier: [],
    optimization_suggestions: [],
    target_allocation: {
      by_geography: [],
      by_property_type: [],
      by_risk_level: [],
      cash_position: 0,
      leverage_ratio: 65
    }
  })

  const tabs = [
    { id: 'overview', name: 'Risk Overview', icon: Target },
    { id: 'concentration', name: 'Concentration Risk', icon: AlertTriangle },
    { id: 'monte-carlo', name: 'Monte Carlo', icon: TrendingUp },
    { id: 'stress-test', name: 'Stress Testing', icon: Settings },
    { id: 'returns', name: 'Risk-Adjusted Returns', icon: TrendingUp },
    { id: 'education', name: 'Risk Education', icon: Info }
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </main>
      </div>
    )
  }

  if (properties.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Properties Found</h3>
            <p className="text-gray-600 mb-6">
              Add properties to your portfolio to access risk assessment features.
            </p>
            <Button onClick={() => window.location.href = '/portfolio'}>
              Go to Portfolio
            </Button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Risk Assessment Dashboard</h2>
            <p className="text-gray-600 mt-1">
              Comprehensive portfolio risk analysis and stress testing
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="secondary"
              onClick={handleRefreshAssessment}
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Analysis
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportRiskReport}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Risk Alerts Bar */}
        <RiskAlertsPanel
          alerts={riskAlerts}
          loading={loading && riskAlerts.length === 0}
          onAcknowledge={async (alertId: string, notes?: string) => {
            try {
              const response = await apiClient.acknowledgeRiskAlert(alertId, notes)
              if (response.success) {
                // Update the alert in state to mark as acknowledged
                setRiskAlerts(prev => prev.map(alert =>
                  alert.alert_id === alertId
                    ? {
                        ...alert,
                        acknowledged: true,
                        acknowledged_at: new Date().toISOString(),
                        acknowledgment_notes: notes
                      }
                    : alert
                ))
                showSuccess('Alert Acknowledged', response.message || 'Alert has been acknowledged successfully.')

                // Optionally refresh alerts after a brief delay to sync with backend
                setTimeout(() => loadRiskAlerts(), 1000)
              } else {
                throw new Error(response.message || 'Failed to acknowledge alert')
              }
            } catch (error) {
              console.error('Failed to acknowledge alert:', error)
              if (error instanceof Error) {
                if (error.message.includes('not yet available') || error.message.includes('not found')) {
                  // Feature not implemented yet - remove locally for better UX
                  setRiskAlerts(prev => prev.filter(alert => alert.alert_id !== alertId))
                  showError('Feature Not Available', 'Alert acknowledgment is not yet implemented in the backend.')
                } else {
                  showError('Acknowledgment Failed', error.message)
                }
              } else {
                showError('Acknowledgment Failed', 'An unexpected error occurred. Please try again.')
              }
            }
          }}
          onDismiss={async (alertId: string, reason?: string) => {
            try {
              await apiClient.dismissRiskAlert(alertId, reason)
              setRiskAlerts(prev => prev.filter(alert => alert.alert_id !== alertId))
              showSuccess('Alert Dismissed', 'Alert has been dismissed successfully.')
            } catch (error) {
              console.error('Failed to dismiss alert:', error)
              if (error instanceof Error && error.message.includes('not yet available')) {
                // Feature not implemented yet - remove locally for better UX
                setRiskAlerts(prev => prev.filter(alert => alert.alert_id !== alertId))
                showError('Feature Not Available', 'Alert dismissal is not yet implemented in the backend.')
              } else {
                showError('Dismissal Failed', 'Failed to dismiss alert. Please try again.')
              }
            }
          }}
        />

        {/* Portfolio Risk Score Overview */}
        {riskAssessment?.risk_metrics && (
          <div className="mb-8">
            <ErrorBoundary
              onError={(error, errorInfo) => {
                console.error('RiskScoreOverview error:', error, errorInfo)
                console.log('Risk Metrics:', riskAssessment.risk_metrics)
                showError('Display Error', 'Error displaying risk score overview. Check console for details.')
              }}
            >
              <RiskScoreOverview
                riskMetrics={riskAssessment.risk_metrics}
                portfolioValue={properties.reduce((sum, p) => sum + (p.purchase_price || 0), 0)}
                propertyCount={properties.length}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as ActiveTab)}
                  className={`
                    flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Debug Info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded text-sm">
            <strong>Debug:</strong> riskAssessment={riskAssessment ? 'loaded' : 'null'},
            risk_metrics={riskAssessment?.risk_metrics ? 'present' : 'missing'},
            properties={properties.length},
            activeTab={activeTab}
          </div>
        )}

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && riskAssessment?.risk_metrics && (
            <div className="space-y-8">
              {/* Alerts Summary */}
              <AlertsSummary alerts={riskAlerts} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ErrorBoundary
                  onError={(error, errorInfo) => {
                    console.error('PortfolioRiskHeatMap error:', error, errorInfo)
                    showError('Component Error', 'Error displaying portfolio heat map.')
                  }}
                >
                  <PortfolioRiskHeatMap
                    geographic_data={(() => {
                      const geoData = riskAssessment.risk_metrics.concentration_risk?.geographic

                      // Production fallback: if locations array is empty but we have debug data indicating data should exist
                      if (geoData && (!Array.isArray(geoData.locations) || geoData.locations.length === 0)) {
                        console.warn('⚠️ Geographic data locations array is empty, checking for fallback data...')

                        try {
                          const debugData = sessionStorage.getItem('riskAssessment_finalGeoData')
                          if (debugData) {
                            const parsed = JSON.parse(debugData)
                            if (parsed.locationsCount > 0) {
                              console.log('🔄 Attempting to recover geographic data from sessionStorage debug...')

                              // Try to reconstruct data from what we can find
                              const fallbackGeoData = {
                                ...geoData,
                                locations: parsed.sampleLocation ? [parsed.sampleLocation] : [],
                                concentration_score: parsed.concentrationScore || geoData.concentration_score || 50,
                                diversification_recommendation: geoData.diversification_recommendation || 'Data recovery attempted - verification needed'
                              }

                              console.log('🔄 Fallback geographic data constructed:', fallbackGeoData)
                              return fallbackGeoData
                            }
                          }
                        } catch (e) {
                          console.warn('Failed to recover geographic data from debug info:', e)
                        }
                      }

                      return geoData || generateDefaultRiskMetrics().concentration_risk.geographic
                    })()}
                    onLocationClick={(location) => console.log('Location clicked:', location)}
                  />
                </ErrorBoundary>
                <div className="space-y-6">
                  <ErrorBoundary
                    onError={(error, errorInfo) => {
                      console.error('ConcentrationRiskVisualizations error:', error, errorInfo)
                      showError('Component Error', 'Error displaying concentration risk.')
                    }}
                  >
                    <ConcentrationRiskVisualizations
                      concentration_risk={riskAssessment.risk_metrics.concentration_risk || generateDefaultRiskMetrics().concentration_risk}
                      compact={true}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'concentration' && riskAssessment?.risk_metrics && (
            <ErrorBoundary
              onError={(error, errorInfo) => {
                console.error('ConcentrationRiskVisualizations error:', error, errorInfo)
                showError('Component Error', 'Error displaying concentration risk analysis.')
              }}
            >
              <ConcentrationRiskVisualizations
                concentration_risk={riskAssessment.risk_metrics.concentration_risk || generateDefaultRiskMetrics().concentration_risk}
                onDrillDown={(category, item) => console.log('Drill down:', category, item)}
              />
            </ErrorBoundary>
          )}

          {activeTab === 'monte-carlo' && (
            <ErrorBoundary
              onError={(error, errorInfo) => {
                console.error('Monte Carlo component error:', error, errorInfo)
                showError('Component Error', 'Monte Carlo simulation component encountered an error. Please refresh the page.')
              }}
            >
              <MonteCarloSimulation
                onRunSimulation={handleRunMonteCarloSimulation}
                results={monteCarloResults || undefined}
              />
            </ErrorBoundary>
          )}

          {activeTab === 'stress-test' && riskAssessment?.risk_metrics && (
            <ErrorBoundary
              onError={(error, errorInfo) => {
                console.error('StressTestScenarioBuilder error:', error, errorInfo)
                showError('Component Error', 'Error displaying stress test scenarios.')
              }}
            >
              <StressTestScenarioBuilder
                scenarios={riskAssessment.risk_metrics.stress_test_results?.scenarios || []}
                onRunScenario={handleRunStressTest}
                onSaveScenario={async (scenario) => {
                  // TODO: Implement scenario saving
                  showSuccess('Scenario Saved', 'Stress test scenario has been saved.')
                }}
              />
            </ErrorBoundary>
          )}

          {activeTab === 'returns' && riskAssessment && (
            <ErrorBoundary
              onError={(error, errorInfo) => {
                console.error('RiskAdjustedReturnsMetrics error:', error, errorInfo)
                showError('Component Error', 'Error displaying risk-adjusted returns.')
              }}
            >
              <RiskAdjustedReturnsMetrics
                risk_adjusted_returns={riskAssessment.risk_adjusted_returns || generateDefaultRiskAdjustedReturns()}
              />
            </ErrorBoundary>
          )}

          {activeTab === 'education' && (
            <EducationalRiskContent />
          )}

          {/* Fallback for when risk assessment is not available */}
          {(activeTab === 'overview' || activeTab === 'concentration' || activeTab === 'stress-test' || activeTab === 'returns') &&
           (!riskAssessment || !riskAssessment.risk_metrics) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-yellow-900 mb-2">Risk Analysis Unavailable</h3>
                <p className="text-yellow-800 mb-4">
                  Risk assessment data is not available. This could be because:
                </p>
                <ul className="text-sm text-yellow-700 text-left max-w-md mx-auto space-y-1">
                  <li>• No properties are in your portfolio yet</li>
                  <li>• The risk assessment API is temporarily unavailable</li>
                  <li>• There was an error loading risk data</li>
                </ul>
                <div className="mt-6 space-x-3">
                  <Button onClick={() => window.location.href = '/portfolio'}>
                    Add Properties
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleRefreshAssessment}
                    disabled={refreshing}
                  >
                    Retry Assessment
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}